import { DefaultValues } from 'react-hook-form';

import {
  CompanyIncorporationEvent,
  CreateEventDto,
  EventFormType,
  EventTypeBackend,
  EventUnion,
  ShareIssuanceEvent,
} from '../../../types/events.types';
import { fromBackendDateFormat } from '../../../utils/from-backend-date-format.util';
import { toBackendDateFormat } from '../../../utils/to-backend-date-format.util';
import { toNumber } from '../../../utils/to-number.util';
import { FormSchema } from '../event-form/event-form.validation';
import {
  mapReverseVestingFormToDto,
  mapReverseVestingToForm,
} from '../event-form/utils/reverse-vesting-form.util';

export type ManageReverseVestingEvent = CompanyIncorporationEvent | ShareIssuanceEvent;

/** Row shape the modal renders, flattened out of the two event types it serves. */
export type ManageReverseVestingRow = {
  considerationPaid: number;
  id: string;
  isTerminatedStakeholder: boolean;
  shareClassName: string;
  sharesCount: number;
  stakeholderName: string;
};

export const isManageReverseVestingEvent = (
  event: EventUnion | null | undefined,
): event is ManageReverseVestingEvent =>
  event?.type === EventTypeBackend.COMPANY_INCORPORATION ||
  event?.type === EventTypeBackend.SHARE_ISSUANCE;

/**
 * Reverse-vesting rows share one form slice per event type, so the whole modal can reuse the
 * event form's schema — and with it `ReverseVestingFields`, its row schemas and its validation
 * messages — instead of growing a parallel set that could drift from the Add flow.
 */
export const manageReverseVestingRowPath = (
  eventType: EventTypeBackend,
  index: number,
):
  | `companyIncorporation.issuances.${number}`
  | `optionalStep.share-issuance.issuanceItems.${number}` =>
  eventType === EventTypeBackend.COMPANY_INCORPORATION
    ? `companyIncorporation.issuances.${index}`
    : `optionalStep.share-issuance.issuanceItems.${index}`;

/** Price each issued share was recorded at — par value on incorporation, share price on issuance. */
export const manageReverseVestingSharePrice = (event: ManageReverseVestingEvent): number =>
  event.type === EventTypeBackend.COMPANY_INCORPORATION
    ? toNumber(event.parValue)
    : toNumber(event.sharePrice);

export const manageReverseVestingRows = (
  event: ManageReverseVestingEvent,
): ManageReverseVestingRow[] => {
  const sharePrice = manageReverseVestingSharePrice(event);

  return event.items.map(({ id, stakeholder, shareClass, sharesCount }) => ({
    id,
    considerationPaid: sharePrice * toNumber(sharesCount),
    isTerminatedStakeholder: Boolean(stakeholder?.isTerminated),
    shareClassName: shareClass?.name ?? '',
    sharesCount: toNumber(sharesCount),
    stakeholderName: stakeholder?.fullName ?? '',
  }));
};

/**
 * Modal header subtitle — the share class of the row the "⋮" menu was opened on. The stakeholder
 * is left out on purpose: the modal edits every issuance row of the event, so naming one holder
 * reads as if only that row were being managed.
 */
export const manageReverseVestingSubtitle = (
  event: ManageReverseVestingEvent | null,
  transactionId: string | undefined,
): string => event?.items.find(({ id }) => id === transactionId)?.shareClass?.name ?? '';

/**
 * Seeds only the slices the modal touches. Everything else in `formSchema` is optional, so the
 * resolver still reaches the row refinements that decide whether Save Changes is enabled.
 *
 * Typed as `DefaultValues<FormSchema>` — the deep-partial shape `reset` takes — rather than
 * asserted whole with `as FormSchema`: the partiality is the point, and a blanket cast would hide
 * a genuine mismatch if the schema's slices are reshaped later.
 */
export const manageReverseVestingFormValues = (
  event: ManageReverseVestingEvent,
): DefaultValues<FormSchema> => {
  const rows = event.items.map(({ id, stakeholder, shareClass, sharesCount, reverseVesting }) => ({
    id,
    stakeholder: {
      id: stakeholder?.id ?? '',
      fullName: stakeholder?.fullName ?? '',
      isTerminated: Boolean(stakeholder?.isTerminated),
    },
    shareClass: { id: shareClass?.id ?? '', name: shareClass?.name ?? '' },
    shares: toNumber(sharesCount),
    reverseVesting: mapReverseVestingToForm(reverseVesting),
  }));

  return {
    initialStep: {
      type:
        event.type === EventTypeBackend.COMPANY_INCORPORATION
          ? EventFormType.COMPANY_INCORPORATION
          : EventFormType.SHARE_ISSUANCE,
      id: event.eventId,
    },
    stepOne: { eventDetails: {} },
    optionalStep:
      event.type === EventTypeBackend.SHARE_ISSUANCE
        ? { 'share-issuance': { issuanceItems: rows } }
        : {},
    stepTwo: {},
    companyIncorporation:
      event.type === EventTypeBackend.COMPANY_INCORPORATION
        ? {
            incorporationDate: fromBackendDateFormat(event.date),
            parValue: String(event.parValue ?? ''),
            issuances: rows,
            additionalNotes: event.additionalNotes ?? '',
          }
        : undefined,
  };
};

export const manageReverseVestingFormRows = (
  event: ManageReverseVestingEvent,
  data: FormSchema,
): NonNullable<FormSchema['companyIncorporation']>['issuances'] =>
  event.type === EventTypeBackend.COMPANY_INCORPORATION
    ? (data.companyIncorporation?.issuances ?? [])
    : (data.optionalStep?.['share-issuance']?.issuanceItems ?? []);

/**
 * Echoes the persisted event back with only the rows' reverse-vesting blocks (and, when the event
 * is still unlocked, their editable issuance fields) taken from the form.
 *
 * Documents and notes come from the event verbatim rather than from the form: this modal has no
 * document step, and on a locked event the backend only accepts a payload where everything except
 * the schedules matches what is stored.
 */
export const buildManageReverseVestingUpdateDto = (
  event: ManageReverseVestingEvent,
  data: FormSchema,
): CreateEventDto => {
  const formRows = manageReverseVestingFormRows(event, data);
  const filesLinksById = new Map(event.items.map((item) => [item.id, item.filesLinks ?? []]));

  // Every stored day is echoed through `fromBackendDateFormat` before `toBackendDateFormat`, never
  // through a bare `new Date(...)`: the latter reads local parts off a UTC-midnight value, so west
  // of UTC each save would post the day before the stored one — moving the event's date one day
  // earlier per save, or being rejected outright on a locked event.
  const echoStoredDate = (value: Date | string) =>
    toBackendDateFormat(fromBackendDateFormat(value));
  const eventDate = echoStoredDate(event.date);

  const items = formRows.map(({ id, stakeholder, shareClass, shares, reverseVesting }) => ({
    ...(id ? { id } : {}),
    stakeholderId: stakeholder.id,
    shareClassId: shareClass.id,
    sharesCount: Number(shares),
    filesLinks: (id && filesLinksById.get(id)) || [],
    reverseVesting: mapReverseVestingFormToDto(reverseVesting),
  }));

  const shared = {
    additionalNotes: event.additionalNotes ?? '',
    filesLinks: event.filesLinks ?? [],
  };

  if (event.type === EventTypeBackend.COMPANY_INCORPORATION) {
    return {
      ...shared,
      type: EventTypeBackend.COMPANY_INCORPORATION,
      companyIncorporation: {
        date: eventDate,
        parValue: String(event.parValue ?? ''),
        items,
      },
    };
  }

  // A Share Issuance row carries its own issue date, which can differ from the event's — echo each
  // row's own, or a save would rewrite every row to the event date. New rows have none to echo, so
  // they take the event's.
  const itemDateById = new Map(event.items.map((item) => [item.id, item.date]));

  return {
    ...shared,
    type: EventTypeBackend.SHARE_ISSUANCE,
    issuance: {
      date: eventDate,
      sharePrice: event.sharePrice,
      valuationId: event.valuation.id,
      items: items.map((item) => {
        const storedDate = item.id ? itemDateById.get(item.id) : undefined;

        return { ...item, date: storedDate ? echoStoredDate(storedDate) : eventDate };
      }),
    },
  };
};
